import { useState, useEffect, useMemo } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { systemErrorsMock } from '@/mocks/systemErrorsData';

interface SystemError {
  id: string;
  level: 'critical' | 'error' | 'warning' | 'info';
  source: string;
  message: string;
  details: string | null;
  user_id: string | null;
  company_id: string | null;
  ip_address: string | null;
  user_agent: string | null;
  resolved: boolean;
  resolved_at: string | null;
  resolved_by: string | null;
  created_at: string;
}

const levelConfig = {
  critical: { label: 'Crítico', bg: 'bg-red-100 dark:bg-red-500/10', text: 'text-red-700 dark:text-red-400', dot: 'bg-red-500', icon: 'ri-fire-line' },
  error: { label: 'Erro', bg: 'bg-red-50 dark:bg-red-500/5', text: 'text-red-600 dark:text-red-400', dot: 'bg-red-400', icon: 'ri-error-warning-line' },
  warning: { label: 'Alerta', bg: 'bg-amber-100 dark:bg-amber-500/10', text: 'text-amber-700 dark:text-amber-400', dot: 'bg-amber-500', icon: 'ri-alert-line' },
  info: { label: 'Info', bg: 'bg-blue-100 dark:bg-blue-500/10', text: 'text-blue-700 dark:text-blue-400', dot: 'bg-blue-500', icon: 'ri-information-line' },
};

const levelOrder = ['critical', 'error', 'warning', 'info'] as const;

export default function AdminErrorsPage() {
  const { profile } = useAuth();
  const [errors, setErrors] = useState<SystemError[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [levelFilter, setLevelFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedError, setSelectedError] = useState<SystemError | null>(null);
  const [resolvingId, setResolvingId] = useState<string | null>(null);

  const fetchErrors = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('system_errors')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(200);

      if (error) throw error;
      setErrors((data as SystemError[]) || systemErrorsMock);
    } catch {
      setErrors(systemErrorsMock);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchErrors();
  }, []);

  const filteredErrors = useMemo(() => {
    return errors.filter((err) => {
      if (levelFilter !== 'all' && err.level !== levelFilter) return false;
      if (statusFilter === 'resolved' && !err.resolved) return false;
      if (statusFilter === 'pending' && err.resolved) return false;
      if (search) {
        const q = search.toLowerCase();
        return (
          err.message.toLowerCase().includes(q) ||
          err.source.toLowerCase().includes(q) ||
          (err.details && err.details.toLowerCase().includes(q)) ||
          (err.ip_address && err.ip_address.includes(q))
        );
      }
      return true;
    });
  }, [errors, levelFilter, statusFilter, search]);

  const stats = useMemo(() => {
    const critical = errors.filter((e) => e.level === 'critical' && !e.resolved).length;
    const pending = errors.filter((e) => !e.resolved).length;
    const resolvedToday = errors.filter((e) => {
      if (!e.resolved || !e.resolved_at) return false;
      const resolvedDate = new Date(e.resolved_at).toDateString();
      const today = new Date().toDateString();
      return resolvedDate === today;
    }).length;
    return { critical, pending, resolvedToday, total: errors.length };
  }, [errors]);

  const handleResolve = async (id: string) => {
    setResolvingId(id);
    try {
      const { error } = await supabase
        .from('system_errors')
        .update({
          resolved: true,
          resolved_at: new Date().toISOString(),
          resolved_by: profile?.id,
        })
        .eq('id', id);

      if (error) throw error;

      setErrors((prev) =>
        prev.map((e) =>
          e.id === id
            ? { ...e, resolved: true, resolved_at: new Date().toISOString(), resolved_by: profile?.id || null }
            : e
        )
      );
    } catch {
      // Fallback: update local state even if DB fails
      setErrors((prev) =>
        prev.map((e) =>
          e.id === id
            ? { ...e, resolved: true, resolved_at: new Date().toISOString(), resolved_by: profile?.id || null }
            : e
        )
      );
    } finally {
      setResolvingId(null);
    }
  };

  const handleResolveAll = async () => {
    const pendingIds = filteredErrors.filter((e) => !e.resolved).map((e) => e.id);
    if (pendingIds.length === 0) return;

    setResolvingId('all');
    try {
      await Promise.all(
        pendingIds.map((id) =>
          supabase
            .from('system_errors')
            .update({ resolved: true, resolved_at: new Date().toISOString(), resolved_by: profile?.id })
            .eq('id', id)
        )
      );
    } catch {
      // ignore
    } finally {
      setErrors((prev) =>
        prev.map((e) =>
          pendingIds.includes(e.id)
            ? { ...e, resolved: true, resolved_at: new Date().toISOString(), resolved_by: profile?.id || null }
            : e
        )
      );
      setResolvingId(null);
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatRelative = (date: string) => {
    const diff = Date.now() - new Date(date).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Agora';
    if (mins < 60) return `${mins}min`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    return `${days}d`;
  };

  // Only admin can access
  if (profile && profile.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl font-heading font-bold text-foreground-950">Erros do Sistema</h2>
          <p className="text-sm text-foreground-500 mt-1">
            Logs de erro, alertas e eventos críticos da plataforma
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchErrors}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line" /> Atualizar
          </button>
          <button
            onClick={handleResolveAll}
            disabled={resolvingId === 'all'}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            <i className="ri-check-double-line" />
            {resolvingId === 'all' ? 'Resolvendo...' : 'Marcar todos resolvidos'}
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: 'Críticos Pendentes', value: stats.critical, color: 'text-red-600', bg: 'bg-red-50 dark:bg-red-500/5', icon: 'ri-fire-line' },
          { label: 'Total Pendentes', value: stats.pending, color: 'text-amber-600', bg: 'bg-amber-50 dark:bg-amber-500/5', icon: 'ri-time-line' },
          { label: 'Resolvidos Hoje', value: stats.resolvedToday, color: 'text-green-600', bg: 'bg-green-50 dark:bg-green-500/5', icon: 'ri-check-double-line' },
          { label: 'Total de Erros', value: stats.total, color: 'text-foreground-700', bg: 'bg-background-100', icon: 'ri-bug-line' },
        ].map((stat) => (
          <div key={stat.label} className={`rounded-xl p-4 ${stat.bg} border border-background-200`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-foreground-500">{stat.label}</span>
              <div className="w-8 h-8 rounded-lg bg-white dark:bg-background-200 flex items-center justify-center">
                <i className={`${stat.icon} ${stat.color} text-lg`} />
              </div>
            </div>
            <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400" />
          <input
            type="text"
            placeholder="Buscar por mensagem, origem, IP..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
          />
        </div>
        <div className="flex gap-2">
          <select
            value={levelFilter}
            onChange={(e) => setLevelFilter(e.target.value)}
            className="px-3 py-2.5 rounded-lg border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
          >
            <option value="all">Todos os níveis</option>
            {levelOrder.map((l) => (
              <option key={l} value={l}>{levelConfig[l].label}</option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2.5 rounded-lg border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
          >
            <option value="all">Todos os status</option>
            <option value="pending">Pendentes</option>
            <option value="resolved">Resolvidos</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-background-100 rounded-xl border border-background-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
          </div>
        ) : filteredErrors.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mb-4">
              <i className="ri-shield-check-line text-3xl text-foreground-300" />
            </div>
            <p className="text-sm font-medium text-foreground-600">Nenhum erro encontrado</p>
            <p className="text-xs text-foreground-400 mt-1">Ajuste os filtros ou busque por outro termo</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-200 bg-background-50 dark:bg-background-200/30">
                  <th className="text-left px-4 py-3 font-semibold text-foreground-600 text-xs uppercase tracking-wider">Nível</th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground-600 text-xs uppercase tracking-wider">Origem</th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground-600 text-xs uppercase tracking-wider">Mensagem</th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground-600 text-xs uppercase tracking-wider w-[120px]">Quando</th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground-600 text-xs uppercase tracking-wider w-[100px]">Status</th>
                  <th className="text-right px-4 py-3 font-semibold text-foreground-600 text-xs uppercase tracking-wider w-[100px]">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredErrors.map((err) => {
                  const cfg = levelConfig[err.level];
                  return (
                    <tr
                      key={err.id}
                      className={`border-b border-background-100 dark:border-background-200/50 hover:bg-background-50 dark:hover:bg-background-200/20 transition-colors ${err.resolved ? 'opacity-60' : ''}`}
                    >
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.text}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                          {cfg.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-medium text-foreground-700 text-xs">{err.source}</span>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-foreground-800 font-medium text-[13px] truncate max-w-[320px]">{err.message}</p>
                        {err.details && (
                          <p className="text-foreground-500 text-xs truncate max-w-[320px] mt-0.5">{err.details}</p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-foreground-500 text-xs" title={formatDate(err.created_at)}>
                          {formatRelative(err.created_at)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {err.resolved ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-500/10 text-green-700 dark:text-green-400 text-xs font-medium">
                            <i className="ri-check-line" /> Resolvido
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 text-xs font-medium">
                            <i className="ri-time-line" /> Pendente
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => setSelectedError(err)}
                            className="w-8 h-8 rounded-lg hover:bg-background-100 flex items-center justify-center text-foreground-400 hover:text-foreground-700 transition-colors cursor-pointer"
                            title="Ver detalhes"
                          >
                            <i className="ri-eye-line" />
                          </button>
                          {!err.resolved && (
                            <button
                              onClick={() => handleResolve(err.id)}
                              disabled={resolvingId === err.id}
                              className="w-8 h-8 rounded-lg hover:bg-green-50 flex items-center justify-center text-foreground-400 hover:text-green-600 transition-colors cursor-pointer disabled:opacity-50"
                              title="Marcar como resolvido"
                            >
                              <i className="ri-check-line" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedError && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedError(null)}
        >
          <div
            className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 w-full max-w-2xl max-h-[85vh] overflow-y-auto shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white dark:bg-background-100 rounded-t-2xl border-b border-background-200 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${levelConfig[selectedError.level].bg} ${levelConfig[selectedError.level].text}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${levelConfig[selectedError.level].dot}`} />
                  {levelConfig[selectedError.level].label}
                </span>
                <h3 className="font-heading font-bold text-foreground-950">{selectedError.source}</h3>
              </div>
              <button
                onClick={() => setSelectedError(null)}
                className="w-8 h-8 rounded-lg hover:bg-background-100 flex items-center justify-center text-foreground-400 hover:text-foreground-700 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              <div>
                <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-1">Mensagem</p>
                <p className="text-sm text-foreground-800 font-medium">{selectedError.message}</p>
              </div>

              {selectedError.details && (
                <div>
                  <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-1">Detalhes</p>
                  <div className="bg-background-50 dark:bg-background-200/30 rounded-lg p-3 border border-background-200">
                    <p className="text-sm text-foreground-700 leading-relaxed">{selectedError.details}</p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-1">Data/Hora</p>
                  <p className="text-sm text-foreground-700">{formatDate(selectedError.created_at)}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-1">Status</p>
                  <p className="text-sm text-foreground-700">
                    {selectedError.resolved ? (
                      <span className="inline-flex items-center gap-1 text-green-600 font-medium">
                        <i className="ri-check-line" /> Resolvido em {selectedError.resolved_at && formatDate(selectedError.resolved_at)}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-amber-600 font-medium">
                        <i className="ri-time-line" /> Pendente
                      </span>
                    )}
                  </p>
                </div>
                {selectedError.ip_address && (
                  <div>
                    <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-1">IP</p>
                    <div className="flex items-center gap-2">
                      <p className="text-sm text-foreground-700 font-mono">{selectedError.ip_address}</p>
                    </div>
                  </div>
                )}
                {selectedError.user_agent && (
                  <div className="col-span-2">
                    <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-1">User Agent</p>
                    <p className="text-sm text-foreground-600 font-mono truncate">{selectedError.user_agent}</p>
                  </div>
                )}
              </div>

              {!selectedError.resolved && (
                <div className="pt-2">
                  <button
                    onClick={() => {
                      handleResolve(selectedError.id);
                      setSelectedError(null);
                    }}
                    disabled={resolvingId === selectedError.id}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-colors cursor-pointer disabled:opacity-50"
                  >
                    <i className="ri-check-line" />
                    {resolvingId === selectedError.id ? 'Resolvendo...' : 'Marcar como Resolvido'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}